// Ported from vlookup-dramatic/app/resources/page.tsx (scripts/revamp/port-design.js),
// then wired to the live Resource Hub calls the old pages made
// (src/Pages/Resourcehub/ResourcesHub.js, Newsletter.js, Video.js).
//
// Addresses (src/Route/route.js -> RoutesRevamp), all rendering this component:
//   /resource-hub/:slug?   Blogs   (slug = all-posts | people-stories | points-of-view | toolkits)
//   /newsletter            Newsletters
//   /videos                Videos
//
// Data (revamp plan, "Existing API Wiring" sheet):
//   Blogs        POST ResourcesHubData/<category>  -> store.ResourcesHubData
//   Newsletters  POST NewsletterData               -> store.NewsletterData
//   Videos       POST VideoData                    -> store.VideoData
// While a call is loading the store holds `true` (that is how the old actions
// signal it); the design's sample cards are never shown as real content.
//
// Plan decisions applied: the "Downloads" tab and the free-text blog TAG strip
// are hidden (nothing behind them); the strip UI is used for the REAL blog
// categories instead. The design's "stage" filter (Early/Mid/Leadership) was
// never rendered by this page.
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useHistory, useLocation, useParams } from "react-router-dom";
import Image from "../../components/image";
import Link from "../../components/link";
import { ButtonLink } from "../../components/button-link";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { ArrowUpRight, Mail, Play, Search, X } from "../../lib/icons";
import { resourcesHero } from "../../lib/content";
import { cn } from "../../lib/utils";
import { usePageMeta } from "../../hooks/use-page-meta";
import * as ResourcesHubActions from "../../../actions/ResourcesHubActions";
import { showToasterWithOk } from "../../../utils/helpers";

/* ---------- config ---------- */

// Grid: 1 col <640, 2 cols 640–1023, 3 cols 1024+. Page size fills the last row.
function pageSizeForWidth() {
  if (typeof window === "undefined") return 9;
  if (window.matchMedia("(min-width: 1024px)").matches) return 9; // 3 × 3
  if (window.matchMedia("(min-width: 640px)").matches) return 10; // 2 × 5
  return 9;
}

function useResourcePageSize() {
  const [pageSize, setPageSize] = useState(pageSizeForWidth);
  useEffect(() => {
    const sync = () => setPageSize(pageSizeForWidth());
    sync();
    const q = window.matchMedia("(min-width: 1024px)");
    const r = window.matchMedia("(min-width: 640px)");
    q.addEventListener("change", sync);
    r.addEventListener("change", sync);
    return () => {
      q.removeEventListener("change", sync);
      r.removeEventListener("change", sync);
    };
  }, []);
  return pageSize;
}

// Blog categories — the same four the old page listed (src/Pages/Resourcehub/ResourcesHub.js).
const CATEGORIES = [
  { name: "All posts", slug: "all-posts" },
  { name: "People stories", slug: "people-stories" },
  { name: "Points of view", slug: "points-of-view" },
  { name: "Toolkits", slug: "toolkits" },
];
const CATEGORY_CHIP = { "people-stories": "People story", "points-of-view": "Point of view", toolkits: "Toolkit" };
const CATEGORY_BY_ID = { 1: "people-stories", 2: "points-of-view", 3: "toolkits" }; // category_id -> slug (see API)

// The three live sections and their addresses. "Downloads" (design) is hidden.
const SECTIONS = [
  { key: "Blogs", label: "Blogs", href: "/resource-hub/all-posts" },
  { key: "Newsletters", label: "Newsletters", href: "/newsletter" },
  { key: "Videos", label: "Videos", href: "/videos" },
];

// Chips sit on photography, so each needs a solid fill to stay legible.
const toneChip = {
  blue: "bg-brand-blue text-white",
  red: "bg-brand-red text-white",
  navy: "bg-brand-navy text-white",
  green: "bg-card text-brand-navy ring-1 ring-brand-navy/10",
};

// Editorial emphasis split for the masthead heading ("Resources Hub" -> "Resources" + italic "Hub").
const heroWords = resourcesHero.heading.split(" ");
const heroEm = heroWords.pop();
const heroLead = heroWords.join(" ");

/* ---------- API -> card shape ---------- */

const blogCard = (b) => ({
  id: "blog-" + b.id,
  section: "Blogs",
  title: b.short_title || b.title,
  author: b.user_name,
  date: b.date,
  img: b.thumb_image || b.image,
  chip: CATEGORY_CHIP[CATEGORY_BY_ID[b.category_id]] || "Blog",
  tone: b.category_id === 3 ? "navy" : b.category_id === 2 ? "blue" : "red",
  href: "/resources-details/" + b.slug, // old detail page, now inside the legacy shell
});

const newsletterCard = (n) => ({
  id: "newsletter-" + n.id,
  section: "Newsletters",
  title: (n.title && n.title.trim()) || "vLookUp Newsletter · " + (n.publish_date_formatted || n.date),
  author: "vLookUp",
  date: "Edition: " + (n.publish_date_formatted || n.date || ""),
  img: n.image,
  chip: "Newsletter",
  tone: "red",
  html: n.description, // opened in a dialog, like the old page
});

const videoCard = (v) => ({
  id: "video-" + v.id,
  section: "Videos",
  title: v.title,
  author: "vLookUp",
  date: v.date || v.publish_date,
  img: v.image,
  chip: "Video",
  tone: "blue",
  videoId: v.video_id, // YouTube id, played in a dialog, like the old page
  url: "https://www.youtube.com/watch?v=" + v.video_id,
});

const asList = (v) => (Array.isArray(v) ? v : null); // `true` = loading

/* ---------- page ---------- */

export default function ResourcesPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const { slug } = useParams();

  const section = pathname.startsWith("/newsletter") ? "Newsletters" : pathname.startsWith("/videos") ? "Videos" : "Blogs";
  const category = CATEGORIES.some((c) => c.slug === slug) ? slug : "all-posts";
  const categoryName = (CATEGORIES.find((c) => c.slug === category) || CATEGORIES[0]).name;

  const blogsRaw = useSelector((s) => s.ResourcesHubData);
  const newslettersRaw = useSelector((s) => s.NewsletterData);
  const videosRaw = useSelector((s) => s.VideoData);
  const isAuth = useSelector((s) => s.isAuth);

  // Blogs reload per category (the endpoint takes it); newsletters/videos once.
  useEffect(() => {
    dispatch(ResourcesHubActions.loadResourcesHubData({ category }));
  }, [dispatch, category]);
  // (initial store value is []; only skip when a previous visit already loaded items)
  useEffect(() => {
    if (!Array.isArray(newslettersRaw) || newslettersRaw.length === 0) dispatch(ResourcesHubActions.loadNewsletterData());
    if (!Array.isArray(videosRaw) || videosRaw.length === 0) dispatch(ResourcesHubActions.loadVideoData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  usePageMeta({
    title: section === "Blogs" ? "Resources" + (category !== "all-posts" ? " (" + categoryName + ")" : "") : "Resources (" + section + ")",
    description: resourcesHero.sub,
  });

  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(null); // newsletter / video item shown in the dialog
  const pageSize = useResourcePageSize();

  // Reset paging/search when the list changes.
  useEffect(() => {
    setPage(1);
    setQuery("");
  }, [section, category]);

  const lists = {
    Blogs: asList(blogsRaw) && blogsRaw.map(blogCard),
    Newsletters: asList(newslettersRaw) && newslettersRaw.map(newsletterCard),
    Videos: asList(videosRaw) && videosRaw.map(videoCard),
  };
  const items = lists[section]; // null while loading
  const loading = items === null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (items || []).filter((item) => q === "" || item.title.toLowerCase().includes(q));
  }, [items, query]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goPage = (n) => {
    setPage(n);
    const grid = document.getElementById("resources-grid");
    if (grid) grid.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // "Submit your write up" — same rules as the old page.
  const submitWriteUp = (e) => {
    e.preventDefault();
    if (!isAuth) {
      showToasterWithOk(
        "Thank you for browsing vLookUp! Help us create a meaningful mentoring engagement for you by registering in one simple step, or login if you already have an account."
      );
      history.push("/login");
      return;
    }
    if (localStorage.isConfirmed === "true") {
      window.location = "/post-resource-hub";
    } else {
      showToasterWithOk("Please Complete Your Profile First.");
    }
  };

  const isBlogs = section === "Blogs";
  const noun = section === "Blogs" ? "post" : section === "Videos" ? "video" : "newsletter";

  return (
    <div className="overflow-x-clip">
      {/* ===================== COMPACT MASTHEAD ===================== */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -right-40 -top-28 -z-10 size-[460px] rounded-full bg-brand-blue/15 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -left-40 top-24 -z-10 size-[380px] rounded-full bg-brand-blue/15 blur-3xl" />

        <div className="wrap pb-8 pt-14 text-center md:pt-20">
          <p className="mb-4 text-xs font-bold uppercase tracking-[0.24em] text-brand-red">Career insights, ideas & toolkits</p>
          <h1 className="mx-auto max-w-4xl font-display text-5xl font-semibold leading-[0.95] text-heading md:text-7xl">
            {heroLead} <em className="font-normal italic text-brand-red">{heroEm}</em>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-muted-foreground">{resourcesHero.sub}</p>
        </div>
      </section>

      {/* ===================== FIRST-LEVEL NAV + SEARCH ===================== */}
      <section className="wrap scroll-mt-28">
        <div className="flex flex-col items-stretch gap-5 lg:flex-row lg:items-center lg:justify-between">
          <nav aria-label="Resource sections" className="flex flex-wrap justify-center gap-2.5 lg:justify-start">
            {SECTIONS.map((s) => {
              const activeSection = s.key === section;
              const list = lists[s.key];
              return (
                <Link
                  key={s.key}
                  href={s.href}
                  aria-current={activeSection ? "page" : undefined}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-bold transition-all",
                    activeSection
                      ? "border-brand-red bg-brand-red text-white shadow-[0_14px_30px_-16px_rgba(239,67,67,0.8)]"
                      : "border-brand-navy/15 bg-card text-brand-navy/70 hover:-translate-y-0.5 hover:border-brand-navy/40 hover:text-brand-navy"
                  )}
                >
                  {s.label}
                  {list && (
                    <span className={cn("text-xs font-bold", activeSection ? "text-white/70" : "text-brand-navy/40")}>
                      · {s.key === "Blogs" && category !== "all-posts" ? "…" : list.length}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Always-available search (client-side, by title) */}
          <div className="relative w-full lg:max-w-xs">
            <Search className="absolute left-5 top-1/2 size-4 -translate-y-1/2 text-brand-red" />
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder={`Search ${section.toLowerCase()}…`}
              aria-label="Search resources by title"
              className="w-full rounded-full border border-brand-navy/15 bg-card py-3 pl-12 pr-5 text-sm font-semibold text-foreground shadow-sm outline-none transition-colors placeholder:font-normal placeholder:text-muted-foreground focus:border-brand-red"
            />
          </div>
        </div>

        {/* Second-level strip (Blogs only): the real categories, address-driven */}
        {isBlogs && <CategoryStrip active={category} className="mt-8 justify-center" />}
      </section>

      {/* ===================== CARD GRID ===================== */}
      <section id="resources-grid" className="wrap mt-8 scroll-mt-28 md:mt-10">
        <div className="flex items-center justify-between gap-4 border-b border-brand-navy/10 pb-4">
          <h2 className="font-display text-2xl font-semibold text-heading md:text-3xl">
            {section}
            {isBlogs && category !== "all-posts" && <em className="ml-2 font-normal italic text-brand-red">/ {categoryName}</em>}
          </h2>
          <span className="whitespace-nowrap text-sm font-bold text-muted-foreground">
            {loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? noun : noun + "s"}`}
          </span>
        </div>

        {loading ? (
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="h-[380px] animate-pulse rounded-[1.75rem] bg-cream-deep ring-1 ring-brand-navy/5" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="mt-8 rounded-[2rem] border border-dashed border-brand-navy/20 bg-card px-8 py-16 text-center">
            <p className="font-display text-2xl font-semibold text-brand-navy">Nothing here yet</p>
            <p className="mt-2 text-muted-foreground">
              {query ? "No " + noun + "s match your search — try another term." : "New " + noun + "s are on their way. Check back soon."}
            </p>
          </div>
        ) : (
          <div key={`${section}-${category}-${query}-${safePage}`} className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((item, i) => (
              <ResourceCard key={item.id} item={item} index={i} onOpen={setOpen} from={pathname} />
            ))}
          </div>
        )}

        {/* Category strip repeated below the grid (Blogs only) */}
        {isBlogs && !loading && (
          <div className="mt-12 border-t border-brand-navy/10 pt-8">
            <p className="mb-4 text-center text-xs font-bold uppercase tracking-[0.22em] text-brand-navy/50">Browse by category</p>
            <CategoryStrip active={category} className="justify-center" />
          </div>
        )}

        {/* Pagination — only when there is more than one page */}
        {!loading && pageCount > 1 && (
          <nav aria-label="Pagination" className="mt-10 flex flex-wrap items-center justify-center gap-2">
            <PageBtn label="‹" ariaLabel="Previous page" disabled={safePage === 1} onClick={() => goPage(safePage - 1)} />
            {pageWindow(safePage, pageCount).map((n, i) =>
              n === "…" ? (
                <span key={"gap-" + i} className="px-1 text-brand-navy/40">…</span>
              ) : (
                <PageBtn key={n} label={String(n)} ariaLabel={`Page ${n}`} active={n === safePage} onClick={() => goPage(n)} />
              )
            )}
            <PageBtn label="›" ariaLabel="Next page" disabled={safePage === pageCount} onClick={() => goPage(safePage + 1)} />
          </nav>
        )}
      </section>

      {/* ===================== NO-DEAD-ENDS CTA BAND ===================== */}
      <section className="wrap py-12 md:py-16">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-cream-deep px-8 py-10 md:px-14 md:py-12">
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-16 size-[260px] rounded-full bg-brand-blue/15 blur-3xl" />
          <div className="relative flex flex-col items-start justify-between gap-7 md:flex-row md:items-center">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl font-semibold text-heading md:text-4xl">
                Have something to <em className="font-normal italic text-brand-red">share?</em>
              </h2>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                Write for the vLookUp community — or get every new resource in your inbox.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <button
                type="button"
                onClick={submitWriteUp}
                className="inline-flex h-auto items-center gap-2 rounded-full border border-brand-navy/20 px-6 py-3.5 text-base font-bold text-brand-navy transition-colors hover:bg-brand-navy hover:text-white"
              >
                <PenLineIcon className="size-4" />
                Submit your write up
              </button>
              <ButtonLink
                href="/newsletter"
                className="group h-auto rounded-full bg-brand-red py-3.5 pl-6 pr-3 text-base font-bold text-white hover:bg-brand-red-dark"
              >
                <span className="inline-flex items-center gap-2">
                  <Mail className="size-4" />
                  Read our newsletters
                </span>
                <span className="ml-3 grid size-8 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                  <ArrowUpRight className="size-4" />
                </span>
              </ButtonLink>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter reader / video player (the old pages used a Bootstrap modal) */}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent
          showCloseButton={false}
          className={cn(
            "max-w-[min(64rem,calc(100%-2rem))] gap-0 overflow-hidden rounded-2xl p-0 shadow-2xl sm:max-w-4xl",
            open && open.videoId ? "border-0 bg-black ring-0" : "bg-card"
          )}
        >
          <DialogTitle className="sr-only">{open ? open.title : ""}</DialogTitle>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(null)}
            className="absolute right-3 top-3 z-10 flex size-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition-colors hover:bg-black/85"
          >
            <X className="size-5" />
          </button>
          {open && open.videoId && (
            <div className="relative aspect-video w-full bg-black">
              {/* clipboard-write keeps YouTube's own "Copy link" working inside
                  the embed (QA: "Unable to copy link to clipboard"). */}
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${open.videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={open.title}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write; web-share"
                allowFullScreen
              />
            </div>
          )}
          {open && open.html && (
            <div className="max-h-[80vh] overflow-y-auto">
              <div className="border-b border-brand-navy/10 px-6 py-4 pr-14 font-display text-lg font-semibold text-heading">{open.title}</div>
              <div
                className="newsletter-html px-6 py-5 text-sm leading-relaxed [&_img]:h-auto [&_img]:max-w-full"
                dangerouslySetInnerHTML={{ __html: open.html }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <style>{`
        @keyframes resHubCardIn {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .res-card-in { animation: resHubCardIn 0.5s ease backwards; }
        @media (prefers-reduced-motion: reduce) {
          .res-card-in { animation: none; }
        }
      `}</style>
    </div>
  );
}

/* ---------- local components ---------- */

// Flat strip of plain-text links — the design's tag strip, used for the real categories.
function CategoryStrip({ active, className }) {
  return (
    <nav aria-label="Blog categories" className={cn("flex flex-wrap items-center gap-y-3", className)}>
      {CATEGORIES.map((c, i) => (
        <Fragment key={c.slug}>
          {i > 0 && (
            <span aria-hidden className="px-2.5 text-brand-navy/20 md:px-3.5">
              /
            </span>
          )}
          <Link
            href={"/resource-hub/" + c.slug}
            aria-current={active === c.slug ? "page" : undefined}
            className={cn(
              "text-xs font-bold uppercase tracking-[0.14em] transition-colors",
              active === c.slug ? "text-brand-red underline decoration-2 underline-offset-[6px]" : "text-brand-navy/55 hover:text-brand-navy"
            )}
          >
            {c.name}
          </Link>
        </Fragment>
      ))}
    </nav>
  );
}

// One uniform editorial card. Blogs link to the detail page; newsletters and
// videos open in the dialog (play badge on videos). Detail links carry
// state.from so the detail page's back control returns exactly here.
function ResourceCard({ item, index, onOpen, from }) {
  const isVideo = !!item.videoId;

  const inner = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-cream-deep to-brand-blue/20">
        <Image
          src={item.img}
          alt={item.title}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 380px"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className={cn(
            "absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.06em]",
            toneChip[item.tone] || toneChip.navy
          )}
        >
          {item.chip}
        </span>
        {isVideo && (
          <>
            <div className="absolute inset-0 bg-brand-ink/30 transition-colors group-hover:bg-brand-ink/45" />
            <span className="absolute left-1/2 top-1/2 grid size-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-brand-red text-white shadow-lg transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 size-6 fill-current" />
            </span>
          </>
        )}
      </div>

      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-display text-xl font-semibold leading-snug text-heading">{item.title}</h3>
        <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <span>{item.author}</span>
          {item.date && (
            <>
              <span aria-hidden>·</span>
              <span>{item.date}</span>
            </>
          )}
        </div>

        <div className="mt-auto border-t border-brand-navy/10 pt-4">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-red">
            {isVideo ? "Watch video" : item.section === "Newsletters" ? "Read newsletter" : "Read more"}
            <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </div>
    </>
  );

  const cardCls =
    "res-card-in group flex h-full w-full flex-col overflow-hidden rounded-[1.75rem] bg-card text-left shadow-sm ring-1 ring-brand-navy/5 transition-all hover:-translate-y-1 hover:shadow-xl";
  const delay = { animationDelay: `${Math.min(index, 8) * 60}ms` };

  if (item.href) {
    return (
      <RouterLink to={{ pathname: item.href, state: { from } }} className={cardCls} style={delay} aria-label={item.title}>
        {inner}
      </RouterLink>
    );
  }
  return (
    <button type="button" onClick={() => onOpen(item)} className={cardCls} style={delay} aria-label={(isVideo ? "Play: " : "Read: ") + item.title}>
      {inner}
    </button>
  );
}

function PageBtn({ label, ariaLabel, active = false, disabled = false, onClick }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "grid size-11 place-items-center rounded-full border text-sm font-bold transition-colors",
        active
          ? "border-brand-red bg-brand-red text-white"
          : disabled
          ? "cursor-not-allowed border-brand-navy/10 bg-card text-brand-navy/25"
          : "border-brand-navy/15 bg-card text-brand-navy hover:border-brand-red hover:text-brand-red"
      )}
    >
      {label}
    </button>
  );
}

// 1 … 4 5 [6] 7 8 … 21 — the live blog list is 180+ posts, so page numbers are windowed.
function pageWindow(current, count) {
  if (count <= 7) return Array.from({ length: count }, (_, i) => i + 1);
  const pages = new Set([1, count, current - 1, current, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => pages.add(n));
  if (current >= count - 2) [count - 3, count - 2, count - 1].forEach((n) => pages.add(n));
  const sorted = [...pages].filter((n) => n >= 1 && n <= count).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push("…");
    out.push(n);
  });
  return out;
}

// lucide "pen-line" — not in lib/icons.js yet (run `npm run revamp:icons PenLine` to add it there).
function PenLineIcon({ className }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M13 21h8" />
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
    </svg>
  );
}
